import type { ProgramPageEpisode } from "../../programPages/types";
import {
  METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE,
  METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT,
  METRICS_PDF_DECK_PAGE_PADDING_TOP,
  METRICS_PDF_FOOTER_RESERVED_PT,
  METRICS_PDF_PAGE_SIZE,
} from "../constants";
import type { CommercialEpisodeGroup } from "./buildCommercialViewsData";

/**
 * Deck pages render with `wrap={false}`, so overflow is clipped rather than
 * flowed and episode tables have to be split up front. Height left for the
 * table on a deck page that carries a footer note.
 */
const CONTENT_HEIGHT_PT =
  METRICS_PDF_PAGE_SIZE[1] -
  METRICS_PDF_DECK_PAGE_PADDING_TOP -
  METRICS_PDF_FOOTER_RESERVED_PT -
  METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE -
  METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT;

/**
 * Costs of the section's own styles, rounded up so a rounding error shows as a
 * spare page rather than a silently clipped row: the totals caption (12 + 10
 * margin) and the table header (11 + 10 padding + border).
 */
const CAPTION_PT = 22;
const TABLE_HEADER_PT = 22;
/** Row height is driven by the 16:9 thumbnail plus vertical padding. */
export const EPISODE_ROW_PT = 30;

export const EPISODES_PER_PAGE = Math.max(
  1,
  Math.floor((CONTENT_HEIGHT_PT - CAPTION_PT - TABLE_HEADER_PT) / EPISODE_ROW_PT),
);

export type CommercialEpisodePage = {
  programId: string;
  name: string;
  channelName?: string;
  imageUrl?: string;
  episodes: ProgramPageEpisode[];
  /** 1-based position among this program's pages. */
  pageIndex: number;
  pageCount: number;
  /** Program-wide totals, so a split program still reports its full figures. */
  totalEpisodes: number;
  totalViews: number;
};

/**
 * One deck page per program, split further when a program has more episodes
 * than fit. Program order is inherited from the summary table.
 */
export function commercialEpisodePages(
  groups: CommercialEpisodeGroup[],
): CommercialEpisodePage[] {
  const pages: CommercialEpisodePage[] = [];

  for (const group of groups) {
    const totalEpisodes = group.episodes.length;
    if (totalEpisodes === 0) continue;

    const totalViews = group.episodes.reduce((sum, e) => sum + e.views, 0);
    const pageCount = Math.ceil(totalEpisodes / EPISODES_PER_PAGE);

    for (let i = 0; i < pageCount; i += 1) {
      pages.push({
        programId: group.programId,
        name: group.name,
        ...(group.channelName ? { channelName: group.channelName } : {}),
        ...(group.imageUrl ? { imageUrl: group.imageUrl } : {}),
        episodes: group.episodes.slice(
          i * EPISODES_PER_PAGE,
          (i + 1) * EPISODES_PER_PAGE,
        ),
        pageIndex: i + 1,
        pageCount,
        totalEpisodes,
        totalViews,
      });
    }
  }

  return pages;
}
